import React from 'react';
import { Card } from 'antd';
import { FiArrowLeft, FiMessageSquare } from 'react-icons/fi';
import { TextLog } from '@/types/TCRMLogs';

interface TextDetailViewProps {
	text: TextLog;
	onBack: () => void;
}

export const TextDetailView: React.FC<TextDetailViewProps> = ({ text, onBack }) => {
	const getDirection = () => {
		return 'Outgoing Message';
	};

	const parseDeliveryStatus = (status: string) => {
		try {
			const parsed = JSON.parse(status);
			if (Array.isArray(parsed)) {
				return parsed.map((item, index) => (
					<div key={index} className="text-sm">
						<span className="font-medium capitalize">{item.status}</span>
						<span className="text-gray-600 ml-2">- {item.timeUtc}</span>
					</div>
				));
			} else if (parsed && typeof parsed === 'object' && parsed.status) {
				return (
					<div className="text-sm">
						<span className="font-medium capitalize">{parsed.status}</span>
						<span className="text-gray-600 ml-2">- {parsed.timeUtc}</span>
					</div>
				);
			}
			return <span className="text-sm">{status}</span>;
		} catch {
			return <span className="text-sm">{status}</span>;
		}
	};

	return (
		<div className="space-y-6">
			<button
				onClick={onBack}
				className="flex items-center gap-2 text-blue-600 hover:text-blue-800 transition-colors"
			>
				<FiArrowLeft className="w-4 h-4" />
				Back to Text Messages
			</button>

			<div className="bg-white rounded-lg border p-6">
				<div className="flex items-center gap-4 mb-6">
					<div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
						<FiMessageSquare className="w-6 h-6 text-blue-600" />
					</div>
					<div>
						<h3 className="text-lg font-semibold">{getDirection()}</h3>
						<p className="text-sm text-gray-600">
							{new Date(text.startedTimestamp).toLocaleString()}
						</p>
						<p className="text-sm text-gray-600">To: {text.to}</p>
					</div>
				</div>

				<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
					<div>
						<label className="block text-sm font-medium text-gray-500">To</label>
						<p className="text-sm text-gray-900">{text.to}</p>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-500">Agent ID</label>
						<p className="text-sm text-gray-900">{text.forwardedFrom}</p>
					</div>
					<div>
						<label className="block text-sm font-medium text-gray-500">Date & Time</label>
						<p className="text-sm text-gray-900">
							{new Date(text.startedTimestamp).toLocaleString()}
						</p>
					</div>
					{text.deliveryStatus && (
						<div>
							<label className="block text-sm font-medium text-gray-500">Delivery Status</label>
							<div className="text-gray-900">{parseDeliveryStatus(text.deliveryStatus)}</div>
						</div>
					)}
				</div>
			</div>

			<Card title="Message Content">
				<div className="bg-gray-50 p-4 rounded-lg">
					<p className="text-sm whitespace-pre-wrap leading-relaxed">{text.text}</p>
				</div>
			</Card>

			{text.workflow && (
				<Card title="Workflow Information">
					<p className="text-sm text-gray-700">{text.workflow}</p>
				</Card>
			)}

			{text.errorText && (
				<Card title="Error Information">
					<div className="bg-red-50 p-4 rounded-lg border border-red-200">
						<p className="text-sm text-red-700">{text.errorText}</p>
					</div>
				</Card>
			)}
		</div>
	);
};
